/* eslint-disable max-lines */
import React, { useState, Fragment, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import moment from 'moment';
import classNames from 'classnames';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import Done from '@material-ui/icons/Done';
import DoneAll from '@material-ui/icons/DoneAll';
import Edit from '@material-ui/icons/Edit';
import Email from '@material-ui/icons/Email';
import Favouriteicon from '@material-ui/icons/Favorite';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import PersonAdd from '@material-ui/icons/PersonAdd';
import PeopleAlt from '@material-ui/icons/PeopleAlt';
import Stars from '@material-ui/icons/Stars';
import { Fan5Widget } from '@pinklabel/widget-fan5';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import FanClubStore from 'stores/FanClubStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import thumbnailStyles from 'components/StreamPlayer/Thumbnail/Thumbnail.css';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import APIStates from 'enum/APIStates';
import {
  GENDER,
  ENUM_FAVOURITE_STATUS,
  ENUM_FRIEND_STATUS,
} from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import {
  generateID,
  isMyProfileHelperFunc,
  shouldShowEditButton,
} from 'utils/helpers';
import useResize from 'hooks/useResize';
import { APPROVAL_STATUS } from 'pages/ImageApprovalTool/consts';
import { getFan5i18n } from 'utils/fan5';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';

import flags from '../../../../../images/logo/flags_new.png';
import temptedLogo from '../../../../../images/logo/temptedlogo.png';
import GalleryStore from '../../ProfileV2Mobile/MobileGallery/GalleryStore';

import HideStreamButton from './HideStreamButton';
import RealityEventButton from './RealityEventButton';
import StarIconFan5 from './StarIconFan5';
import styles from './ProfileBanner.css';


const IDS = {
  prefix: 'NeMaMID',
  element: 'ProfileBanner',
  favourite: 'favourite',
  addFriend: 'addFriend',
  privateTip: 'privateTip',
  privateMessage: 'privateMessage',
  countryFlag: 'countryFlag',
};

const SMALL_DESKTOP_THRESHOLD = 1200;
const ACTION_PARAM = 'action';
const ADD_TO_FAVOURITES = 'addToFavorites';

const makeId = generateID(IDS.prefix, IDS.element);

// eslint-disable-next-line complexity
function ProfileBanner(props) {
  const [isMenuOpen, setIsMenuOpen] = useState(null);
  const { user, setShowRegistrationModal, setIsAnyModalShown, history } = props;
  const { username, accessLevel, email: userEmail } = user;
  const userId = user.user.getIn(['user', 'userId']);
  const {
    favoritedCount,
    friendsCount,
    handleToggleFavorite,
    handleAddFriend,
    hasNewFanClub,
    isLoadingPerformerFavouriteStatus,
    isPlayerBroadcastMode,
    performerDetails,
    performerDetails: { profileImageUrl },
    performerFavouriteStatus,
    performerFriendStatus,
    performerOnline,
    profileInfo: { birthdate, countryId, gender, allowFriendRequests },
    profileName,
    realityShow,
    isRealityEventButtonVisible,
    externalProfile,
  } = ProfileStore;
  const {
    isFan,
    performerFanClubInfo: { activated },
  } = FanClubStore;
  const { userKickedStatus, isBanned } = FirebaseStore;

  const isMyProfile = isMyProfileHelperFunc(user, profileName);
  const showFanclubButton = (activated || hasNewFanClub) && !isMyProfile;
  const playerBroadcastMode = UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER);
  const showSmallBanner = performerOnline && (!isMyProfile || playerBroadcastMode);
  const showBroadcasterHelperText = performerOnline && isMyProfile && playerBroadcastMode;
  const headerClass = showSmallBanner ? styles.onlineHeader : styles.offlineHeader;
  const years = moment().diff(birthdate, 'years');

  let genderForTranslation = gender || GENDER.MALE;
  if (gender === GENDER.SHEMALE) {
    genderForTranslation = GENDER.TRANSGENDER;
  }

  const translatedGender = translate(
    `v2profile.profileInfo.gender.${genderForTranslation}`
  );
  const translatedCountry = countryId ? translate(`countries.${countryId}`) : '';
  const charCode = 8226;
  const separator = String.fromCharCode(charCode);
  const isAdmin = accessLevel >= ACCESS_LEVEL_NUM.ADMIN;

  let buttonStyles = isFan ? {
    label: translate('v2profile.profile.banner.fan'),
    colour: 'blocked',
    icon: <DoneAll />,
  } : {
    label: translate('v2profile.profile.banner.join'),
    colour: 'orange',
    icon: <Stars />,
  };

  if (hasNewFanClub) {
    buttonStyles = {
      label: translate('profile.fanclub.viewFanClub', undefined, 'View Fan Club'),
      colour: 'orange',
      icon: <Stars />,
    };
  }

  const favBtnClass = classNames({
    [styles.profileButton]: true,
    [styles.favorited]: performerFavouriteStatus === ENUM_FAVOURITE_STATUS.FAVORITE,
  });
  const methodAction = {
    [ENUM_FRIEND_STATUS.NONE]: { label: translate('v2profile.button.addFriend'), icon: <PersonAdd /> },
    [ENUM_FRIEND_STATUS.PENDING]: { label: translate('v2profile.button.pending'), icon: <Done /> },
    [ENUM_FRIEND_STATUS.ACCEPTED]: { label: translate('v2profile.button.friendship'), icon: <DoneAll /> },
  };
  const friendsLabel = !isMyProfile ? methodAction[performerFriendStatus].label : friendsCount;
  const friendsIcon = !isMyProfile ? methodAction[performerFriendStatus].icon : <PeopleAlt />;
  const requestSentOrAccepted =
    performerFriendStatus === ENUM_FRIEND_STATUS.PENDING || performerFriendStatus === ENUM_FRIEND_STATUS.ACCEPTED;
  const { isLoadingFriendsButton, isLoadingFavoritesButton } = ProfileStore;
  const blockFriendRequests = UserStore.hasCF(allUserGroups.BLOCK_FRIEND_REQUESTS);
  const showAddFriendButton = (blockFriendRequests && allowFriendRequests) || !blockFriendRequests;
  const kickedOrBanned = userKickedStatus || isBanned;
  const { user: { sfw } } = UserStore;
  const { profileBannerPic } = GalleryStore;

  const location = useLocation();
  const actionValue = new URLSearchParams(location.search).get(ACTION_PARAM);

  const [isFan5Loaded, setIsFan5Loaded] = useState(false);
  const [userHasFan5Account, setUserHasFan5Account] = useState(false);
  const [performerHasFan5Account, setPerformerHasFan5Account] = useState(false);

  const fan5Widget = useRef(null);
  const fan5Flag = performerOnline ? allUserGroups.FAN5_VIEWER : allUserGroups.FAN5_BROADCASTER;
  const showFan5 = UserStore.hasProfileCF(fan5Flag);

  useEffect(() => {
    try {
      if (showFan5) {
        fan5Widget.current = new Fan5Widget({
          'type': Fan5Widget.TYPE_CUSTOMER,
          'broadcaster_username': profileName,
          'customer_id': userId,
          'customer_username': username,
          'customer_email': userEmail,
          'options': {
            'modal_bg_class_name': styles.fan5ModalBG,
            'modal_wrapper_class_name': styles.fan5Modal,
            'i18n': getFan5i18n(translate),
          },
        });
        Fan5Widget.customerHasAccount(userEmail)
          .then((resp) => setUserHasFan5Account(resp))
          .catch(() => undefined);
        Fan5Widget.broadcasterHasAccount(profileName)
          .then((resp) => setPerformerHasFan5Account(resp))
          .catch(() => undefined);
        fan5Widget.current.on('error', () => undefined);
        setIsFan5Loaded(true);
      }
    } catch (err) {
      fan5Widget.current = null;
      setIsFan5Loaded(false);
    }

    return () => {
      fan5Widget.current = null;
    };

  }, [profileName, username, userEmail, showFan5, userId]);

  const openLoginModal = useCallback(() => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.LOGIN_MODAL_V2,
      {
        handleOpenRegistrationModal: () => setShowRegistrationModal(true, RegistrationModalCaller.PROFILE_BANNER_LOGIN),
        setIsAnyModalShown,
      }
    );

    if (setIsAnyModalShown) {
      setIsAnyModalShown(true);
    }
  }, [setIsAnyModalShown, setShowRegistrationModal]);

  const toggleActionWrapper = useCallback((fn, registrationModalCaller, login = false) => () => {
    if (username) {
      if (isMyProfile) {
        return;
      }

      const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
      if (isGuestBilling) {
        ModalService.showModal(
          ModalService.MODAL_KEYS.GUEST_BILLING_MODAL,
          {
            close: () => ModalService.hideModal(),
            show: true,
          }
        );

        return;
      }

      fn(username);
    } else if (login) {
      openLoginModal();
    } else {
      setShowRegistrationModal(true, registrationModalCaller);
    }
  }, [
    accessLevel,
    isMyProfile,
    openLoginModal,
    setShowRegistrationModal,
    username,
  ]);

  useEffect(() => {
    if (actionValue === ADD_TO_FAVOURITES
      && (isLoadingPerformerFavouriteStatus === APIStates.LOADED || !username)) {

      if (profileName === username) {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2profile.favorite.self'),
          subtype: NotificationService.subtype.SUCCESS,
        });

      } else if (performerFavouriteStatus === ENUM_FAVOURITE_STATUS.NOT_FAVORITE) {
        toggleActionWrapper(handleToggleFavorite, RegistrationModalCaller.PROFILE_BANNER_ADD_TO_FAVORITES, true);

      } else {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2profile.favorite.already.added'),
          subtype: NotificationService.subtype.SUCCESS,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, isLoadingPerformerFavouriteStatus]);

  const handleJoinFanClub = useCallback(() => {
    const { isGuestBilling } = UserStore;
    if (isGuestBilling) {
      ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL, {
        close: () => ModalService.hideModal(),
        show: true,
      });
    } else {
      if (hasNewFanClub) {
        history.push(routeDefinitions.get(routeDefinitions.profileV2.fanclub, { user: profileName }));

        return;
      }
      ModalService.showModal(ModalService.MODAL_KEYS.JOIN_FAN_CLUB_MODAL);
    }
  }, [hasNewFanClub, history, profileName]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(null);
  }, []);

  const sendTip = useCallback(() => {
    handleMenuClose();
    ProfileStore.initiateTip(profileName);
  }, [profileName, handleMenuClose]);

  const sendPrivateMessage = useCallback(() => {
    handleMenuClose();
    const privateMessageProps = {
      profilePicture: profileImageUrl,
      receiverUsername: profileName,
      user,
    };
    ProfileStore.initiatePrivateMessage(privateMessageProps);
  }, [profileImageUrl, profileName, user, handleMenuClose]);

  const galleryRoute = routeDefinitions.get(routeDefinitions.profileV2.gallery, { user: profileName });

  const handleProfileImageClick = useCallback(() => {
    const profileBannerPicStatus = profileBannerPic.map((profileBannerPicInfo) => profileBannerPicInfo.status);
    if (profileBannerPicStatus[0] !== APPROVAL_STATUS.SFW && profileBannerPicStatus[0] !== APPROVAL_STATUS.NSFW) {
      return;
    }

    if (GalleryStore.hasPhotos) {
      if (history.location.pathname === galleryRoute) {
        const state = { ...history.location.state };
        state.openProfileGallery = true;
        history.replace({ ...history.location, state });
      } else {
        history.push({
          pathname: galleryRoute,
          state: { openProfileGallery: true },
        });
      }
    }
  }, [galleryRoute, history, profileBannerPic]);

  const onOpenFan5Widget = useCallback(() => {
    fan5Widget.current.open();
    window.scroll(0, 0);
  }, [fan5Widget]);

  const friendBtnClass = classNames({
    [styles.profileButton]: true,
    [styles.profileButtonActive]: requestSentOrAccepted,
  });

  const fanClubBtnClass = classNames(styles.fanClubButton, {
    [styles.profileButton]: true,
    [styles.profileButtonActive]: isFan,
  });

  const fan5BtnClass = classNames({
    [styles.profileButton]: true,
    [styles.fan5Button]: true,
  });

  const profileBannerPicURL = profileBannerPic[0]?.mainImageUrl;
  const profileOffline = !performerOnline || (isMyProfile && !playerBroadcastMode);
  const profilePictureClasses = classNames({
    [styles.profilePicture]: profileOffline,
    [styles.profilePictureSmall]: !profileOffline,
    [styles.profilePicturePointer]: GalleryStore.hasPhotos && !realityShow?.mainEvent,
    [styles.blur]: sfw,
    [styles.defaultImage]: !profileBannerPicURL,
  });

  const editPictureRoute = routeDefinitions.get(routeDefinitions.galleries.root, { username });

  const handleMenuClick = useCallback((e) => {
    setIsMenuOpen(e.currentTarget);
  }, []);
  const [width] = useResize();
  const smallDesktop = width < SMALL_DESKTOP_THRESHOLD;

  const fan5btnTextJoin = translate('fan5.btn.join');
  const fan5btnTextOpen = translate('fan5.btn.open');
  const fan5btnText = userHasFan5Account ? fan5btnTextOpen : fan5btnTextJoin;
  const useDefault = !profileBannerPicURL;
  const profileImageStyle = { backgroundImage: useDefault ? `url(${profileBannerPicURL})` : `url(${profileImageUrl})` };

  const editButtonElement = useMemo(() => {
    if (!shouldShowEditButton(user, profileName)) {
      return null;
    }

    return (
      <Link className={styles.editIcon} to={editPictureRoute}>
        <Edit />
      </Link>
    );
  }, [user, profileName, editPictureRoute]);

  const countryElement = useMemo(() => {
    if (!countryId) {
      return null;
    }

    return (
      <Tooltip
        arrow={true}
        classes={{ tooltip: styles.tooltip, arrow: styles.arrow }}
        title={translate(`countries.${countryId}`)}
      >
        <span
          className={`${styles.flag} ${thumbnailStyles[`flag-${countryId}`]}`}
          id={makeId(IDS.countryFlag)}
          style={{ backgroundImage: `url("${flags}")` }}
        />
      </Tooltip>
    );
  }, [countryId]);

  const eventTenantName = useMemo(() => {
    if (!externalProfile?.isBroadcastingFromExternal) {
      return null;
    }

    return (
      <span className={styles.tenantWrap}>
        {'-'}
        <a
          className={styles.tenant}
          href={routeDefinitions.temptedNakedAndUncut}
          rel="noopener noreferrer"
          target="_blank"
        >
          Tempted
          <img
            alt="temptedLogo"
            className={styles.temptedLogo}
            src={temptedLogo}
          />
        </a>
      </span>
    );
  }, [externalProfile?.isBroadcastingFromExternal]);


  const adminFlagElement = useMemo(() => {
    if (!isAdmin) {
      return null;
    }

    return (
      <span className={styles.admCtrl}>
        {` - ${translate('strings.CamRoom.headerView.admin')}`}
      </span>
    );
  }, [isAdmin]);

  if (realityShow?.mainEvent) {
    const contentClassName = showSmallBanner ? styles.contentEvent : styles.content;

    return (
      <div className={headerClass}>
        <div className={styles.tint} />
        <div className={styles.center}>
          <div className={contentClassName}>
            <div
              className={profilePictureClasses}
              style={!useDefault ? profileImageStyle : {}}
            />
            {editButtonElement}

            <h1 className={styles.name}>
              <span className={styles.performerName}>
                {performerDetails.username}
              </span>

              {countryElement}
              {adminFlagElement}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={headerClass}>
      <div className={styles.tint} />
      <div className={styles.center}>
        <div className={showSmallBanner ? styles.contentSmall : styles.content}>
          <div
            className={profilePictureClasses}
            style={!useDefault ? profileImageStyle : {}}
            onClick={handleProfileImageClick}
          />
          {editButtonElement}

          <h1 className={styles.name}>
            <span className={styles.performerName}>
              {performerDetails.username}
            </span>
            {countryElement}
            {eventTenantName}
            {adminFlagElement}
          </h1>

          {profileOffline && (
            <div className={styles.info}>
              {birthdate && `${years} ${separator}`} {translatedGender} {separator} {translatedCountry}
            </div>
          )}

          {!kickedOrBanned && (
            <div className={showSmallBanner ? styles.buttonsContainerSmall : styles.buttonsContainer}>
              {(isFan5Loaded && performerHasFan5Account) && (
                <Button
                  className={fan5BtnClass}
                  icon={<StarIconFan5 className={styles.fan5Image} />}
                  id="v2joinFan5"
                  label={fan5btnText}
                  size="small"
                  onClick={onOpenFan5Widget}
                />
              )}

              {showFanclubButton && (
                <Button
                  className={fanClubBtnClass}
                  colour={buttonStyles.colour}
                  disabled={hasNewFanClub ? false : isFan}
                  icon={buttonStyles.icon}
                  id="v2JoinFanClubButton"
                  label={buttonStyles.label}
                  size="small"
                  onClick={handleJoinFanClub}
                />
              )}
              <Tooltip
                arrow={true}
                classes={{ tooltip: styles.tooltip, arrow: styles.arrow }}
                open={isPlayerBroadcastMode ? false : null}
                title={translate('v2profile.favorite.tooltip.text', { 0: performerDetails.username })}
              >
                <div>
                  <LoadingButton
                    className={favBtnClass}
                    colorVariant="white"
                    customContainerClass={styles.buttonContainer}
                    disabled={isMyProfile}
                    id={makeId(IDS.favourite)}
                    isLoading={isLoadingFavoritesButton}
                    size="small"
                    startIcon={!isLoadingFavoritesButton && <Favouriteicon />}
                    onClick={toggleActionWrapper(handleToggleFavorite,
                      RegistrationModalCaller.PROFILE_BANNER_FAVORITE_BUTTON)}
                  >
                    {favoritedCount}
                  </LoadingButton>
                </div>
              </Tooltip>
              {showAddFriendButton && (
                <LoadingButton
                  className={friendBtnClass}
                  colorVariant="white"
                  customContainerClass={styles.buttonContainer}
                  disabled={isMyProfile}
                  id={makeId(IDS.addFriend)}
                  isLoading={isLoadingFriendsButton}
                  size="small"
                  startIcon={!isLoadingFriendsButton && friendsIcon}
                  onClick={toggleActionWrapper(handleAddFriend,
                    RegistrationModalCaller.PROFILE_BANNER_FRIEND_BUTTON)}
                >
                  {friendsLabel}
                </LoadingButton>
              )}
              {showBroadcasterHelperText && (
                <HideStreamButton />
              )}
              {!isMyProfile && !smallDesktop && (
                <Fragment>
                  <Button
                    className={styles.profileButton}
                    colour="white"
                    icon={<MonetizationOnOutlinedIcon />}
                    id={makeId(IDS.privateTip)}
                    label={translate('v2profile.private.tip')}
                    size="small"
                    onClick={sendTip}
                  />

                  <Button
                    className={styles.profileButton}
                    colour="white"
                    icon={<Email />}
                    id={makeId(IDS.privateMessage)}
                    label={translate('v2profile.inbox.message.label')}
                    size="small"
                    onClick={sendPrivateMessage}
                  />

                  {isRealityEventButtonVisible && (
                    <RealityEventButton user={user} />
                  )}
                </Fragment>
              )}
              {!isMyProfile && smallDesktop && (
                <div className={styles.horizMenu}>
                  <Button
                    aria-controls="privateTipAndMsgMenu"
                    aria-haspopup="true"
                    className={styles.menuBtn}
                    onClick={handleMenuClick}
                  >
                    <MoreHorizIcon className={styles.menuIcon} />
                  </Button>
                  <Menu
                    anchorEl={isMenuOpen}
                    classes={{ paper: styles.privateTipAndMsgMenu }}
                    id="privateTipAndMsgMenu"
                    keepMounted={true}
                    open={Boolean(isMenuOpen)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem className={styles.privateTipMenuBtn} id={makeId(IDS.privateTip)} onClick={sendTip}>
                      <MonetizationOnIcon />
                      {translate('v2profile.private.tip')}
                    </MenuItem>

                    <MenuItem
                      className={styles.privateTipMenuBtn}
                      id={makeId(IDS.privateMessage)}
                      onClick={sendPrivateMessage}
                    >
                      <Email />
                      {translate('v2profile.inbox.message.label')}
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ProfileBanner.propTypes = {
  setIsAnyModalShown: PropTypes.func.isRequired,
  setShowRegistrationModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  history: PropTypes.object,
};

ProfileBanner.defaultProps = { history: null };

export default observer(ProfileBanner);
